import { Component, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { latLng, Map } from 'leaflet';
import { LngLat, LngLatBounds, Marker, Popup } from 'mapbox-gl';
import { MapboxComponent } from '../../shared/mapbox/mapbox.component';
import { Answer, Inspection } from '../../shared/interfaces';
import { MergeAnswersService } from '../../shared/merge.answers.service';
import { AnswerStatus } from '../answer.status';
import { InspectionFilters, ReviewNavigationComponent } from '../review.navigation/review.navigation.component';
import { NgxGalleryOptions } from 'ngx-gallery-14';
import { env } from '../../../environments/environment';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-inspection-locations',
  templateUrl: './inspection-locations.component.html',
  styleUrls: ['./inspection-locations.component.scss'],
})
export class InspectionLocationsComponent implements OnInit {
  @Input() inspection: Inspection;
  @Input() answers: Answer[];
  @Input() filters: InspectionFilters;
  @Input() navigationComponent: ReviewNavigationComponent;

  @ViewChild('popupContainer') popupContainer: any;
  popup: any;

  lLayers: any[] = [];
  map: Map = null;
  showItemGallery = false;
  showSectionGallery = false;

  lOptions = {
    zoom: 17,
    center: latLng(32.98, -95.3),
    minZoom: 13,
    maxZoom: 20,
  } as any;

  galleryOptions: NgxGalleryOptions[];

  constructor(
    private route: ActivatedRoute,
    private mergeAnswersService: MergeAnswersService,
    private zone: NgZone,
    private router: Router,
    private sanitization: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.galleryOptions = [
      {
        image: false,
        width: '310px',
        height: '40px',
        thumbnailsColumns: 6,
        thumbnailsRemainingCount: true,
        thumbnailsMargin: 3,
        thumbnailMargin: 3,
      },
    ];
  }

  async ngOnChanges(changes) {
    if (this.answers && this.inspection) {
      await this.setupAnswers();
      this.applyConfig();
      this.setMarkers();
    } else if ('filters' in changes && this.inspection) {
      this.applyConfig();
      this.setMarkers();
    }
  }

  gotoQuestion(x) {
    this.router.navigate([`/inspection/review/main/`, this.inspection.id], {
      queryParamsHandling: 'merge',
      queryParams: this.getLinkParams(x),
    });
  }

  getLinkParams(answer) {
    if (answer) {
      const item = this.items.indexOf(answer.question.section.item);
      const section = this.items[item].sections.indexOf(answer.question.section);
      const question = this.items[item].sections[section].questions.indexOf(answer.question);
      return {
        item,
        section,
        question,
        right: 'question',
        currentImage: undefined,
      };
    }
  }

  hasLocation = false;
  items = [];
  selectedAnswer: Answer = null;

  // tslint:disable: forin
  mergeAnswers() {
    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;
        section.item = item;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          question.section = section;

          if (question.repeat && question.repeat.length) {
            question.repeat.forEach(itemId => {
              const filteredAnswers = this.answers.filter(ans => {
                return ans.questionId === question.id;
              });

              filteredAnswers.forEach(a => (a.question = question));
              question.answer = filteredAnswers;
            });
          } else {
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });

            if (answer) {
              answer.question = question;
              question.answer = answer;
            }
          }
        }
      }
    }
  }

  preloadImage(url: string) {
    const img = new Image();
    img.src = url;
  }

  getItemPictures(item) {
    const pictures = [];
    for (const section of item.sections) {
      for (const question of section.questions) {
        if (question.answer) {
          const answers = [];
          if (Array.isArray(question.answer)) {
            answers.push(...question.answer);
          } else {
            answers.push(question.answer);
          }
          for (const answer of answers) {
            for (const pic of answer.pictures) {
              const picUrl = `${env.APIUrl}/image/embedded/${pic.id}`;
              if (!pictures.find(x => x.small === picUrl)) {
                this.preloadImage(picUrl);
                pictures.push({
                  small: picUrl,
                  medium: picUrl,
                  big: picUrl,
                });
              }
            }
          }
        }
      }
    }
    return pictures;
  }

  getSectionPictures(section) {
    const pictures = [];
    for (const question of section.questions) {
      if (question.answer) {
        const answers = [];
        if (Array.isArray(question.answer)) {
          answers.push(...question.answer);
        } else {
          answers.push(question.answer);
        }
        for (const answer of answers) {
          for (const pic of answer.pictures) {
            const picUrl = `${env.APIUrl}/image/embedded/${pic.id}`;
            this.preloadImage(picUrl);
            pictures.push({
              small: picUrl,
              medium: picUrl,
              big: picUrl,
            });
          }
        }
      }
    }
    return pictures;
  }

  async setupAnswers() {
    this.items = [...(await this.mergeAnswersService.loadItems(this.inspection, this.answers))];
    this.mergeAnswers();
  }

  filteredAnswers = [];

  applyConfig() {
    this.filteredAnswers = [...(this.answers || [])]
      .filter(a => a.location)
      .filter(answer => {
        if (this.filters) {
          const item = answer.question.section.item.label;
          const section = answer.question.section.id;
          const comments = answer.comments.length;
          const question = answer.question;
          const flags = answer.flags.length;

          if (this.filters.flags && this.filters.flags !== 'all') {
            if (
              !answer.flags.some(flag => this.filters.flags !== 'all' && this.filters.flags.includes(flag.subtypeId))
            ) {
              return false;
            }
          }

          if (!this.filters.withoutFlags && !flags) {
            return false;
          }

          if (!this.filters.withoutComments && !comments) {
            return false;
          }

          if (!this.filters.accepted) {
            const isAccepted = answer.status === AnswerStatus.ACCEPTED;
            if (isAccepted) {
              return false;
            }
          }

          if (!this.filters.unreviewed) {
            const isNotReviewed = answer.status === AnswerStatus.NOT_REVIEWED;
            if (isNotReviewed) {
              return false;
            }
          }

          if (!this.filters.rejected) {
            const isRejected = answer.status === AnswerStatus.REJECTED;
            if (isRejected) {
              return false;
            }
          }

          const isMandatory = answer.question.mandatoryReview;
          const isScored = answer.flaggingScore;

          if (this.filters.questions === 'mandatory' && !isMandatory) {
            return false;
          }

          if (this.filters.questions === 'scored' && !isScored) {
            return false;
          }

          if (this.filters.questions === 'mandatoryOrScored' && !(isScored || isMandatory)) {
            return false;
          }

          if (this.filters.questions === 'autoScored') {
            if (!isScored) {
              return false;
            }
            if (!answer.flags.find((x: any) => x.systemFlag)) {
              return false;
            }
          }

          if (!this.filters.items.includes(item)) {
            return false;
          }

          if (!this.filters.sections.includes(section)) {
            return false;
          }

          const questionTagIds = question.questionTags.map(x => x.id);
          const hasQuestionTags = this.filters.questionTags.length > 0;
          const noQuestionTag = this.filters.questionTags.includes(0);
          const questionHasTags = question.questionTags.length > 0;
          const hasMoreThanOneTag = this.filters.questionTags.length > 1;
          const noMatchingTags = !this.filters.questionTags.some(element => questionTagIds.includes(element));

          if (!hasQuestionTags) {
            return false;
          }

          if (noQuestionTag) {
            if (hasMoreThanOneTag) {
              return !(questionTagIds.length > 0 && noMatchingTags);
            } else {
              return !questionHasTags;
            }
          } else {
            return !(questionTagIds.length === 0 || (questionTagIds.length > 0 && noMatchingTags));
          }
        }
        return true;
      });

    if (this.filters) {
      if (this.filters.sorting === 'followConfigurationOrder') {
        this.filteredAnswers.sort((a, b) => {
          return a.questionId - b.questionId;
        });
      } else {
        this.filteredAnswers.sort((a, b) => {
          const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          return this.filters.sorting === 'asc' ? diff : -diff;
        });
      }
    }

    this.setMarkers();
  }

  mapbox: MapboxComponent;

  setMap(mapbox: MapboxComponent) {
    this.mapbox = mapbox;
    this.setMarkers();
  }

  scoreChanged(data) {
    this.applyConfig();
    const score = [];
    if (data.score0) {
      score.push(0);
    }
    if (data.score1) {
      score.push(1);
      score.push(3);
    }
    if (data.score2) {
      score.push(5);
      score.push(10);
    }
    if (data.score3) {
      score.push(100);
    }
    this.filteredAnswers = this.filteredAnswers.filter(answer => {
      return score.includes(answer.flaggingScore);
    });
    this.setMarkers();
  }

  setMarkers() {
    this.mapbox?.removeMarkers();

    this.filteredAnswers?.forEach(answer => {
      const item = answer?.question?.section?.item?.label;
      const section = answer?.question?.section?.name;
      answer.label = `${item} / ${section}`;

      if (answer.location) {
        this.hasLocation = true;
        const markerColor = this.getColorBasedOnScore(answer.flaggingScore);
        this.addMarker(answer, markerColor);
      }
    });
  }

  getColorBasedOnScore(flaggingScore) {
    if (flaggingScore === 0) {
      return 'green';
    } else if (flaggingScore === 1 || flaggingScore === 3) {
      return 'yellow';
    } else if (flaggingScore === 5 || flaggingScore === 10) {
      return 'orange';
    } else if (flaggingScore === 100) {
      return 'red';
    }
  }

  addMarker(answer: Answer, markerColor: string) {
    const center = answer.location;
    const marker = new Marker({ draggable: false, color: markerColor }).setLngLat(center);
    this.mapbox.addMarker(marker);

    marker.getElement().addEventListener('click', () => {
      this.zone.run(() => {
        this.selectedAnswer = answer;

        const item = answer.question.section.item.label;
        const section = answer.question.section.name;
        const question = answer.question.text;

        if (!this.mapbox) {
          return;
        }

        const itemImages = this.getItemPictures(answer.question.section.item);
        const sectionImages = this.getSectionPictures(answer.question.section);

        this.popup = {
          item,
          section,
          question,
          center,
          itemImages,
          sectionImages,
        };

        const popup = new Popup({ offset: 30 }).setDOMContent(this.popupContainer.nativeElement).setMaxWidth('430px');

        popup.on('open', () => {
          const closeButton = document.querySelector('.mapboxgl-popup-close-button');
          if (closeButton instanceof HTMLElement) {
            closeButton.style.display = 'none';
          }
        });

        marker.setPopup(popup);
        marker.getPopup().on('close', () => {
          this.showItemGallery = false;
          this.showSectionGallery = false;
        });
      });
    });

    this.mapbox.jumpTo({
      zoom: 15,
      center,
    });

    this.mapbox.map.setMaxBounds(
      new LngLatBounds(new LngLat(center[0] - 0.02, center[1] - 0.02), new LngLat(center[0] + 0.02, center[1] + 0.02)),
    );
  }

  setItem(answer: Answer) {
    const item = answer.question?.section?.item?.label;
    this.navigationComponent.filters.items = [item];
    this.navigationComponent.applyFilters();
  }

  setSection(answer: Answer) {
    const section = answer?.question?.section;
    this.navigationComponent.filters.sections = [section.id];
    this.navigationComponent.applyFilters();
  }

  toggleItemGallery() {
    this.showItemGallery = !this.showItemGallery;
    this.showSectionGallery = false;
  }

  toggleSectionGallery() {
    this.showSectionGallery = !this.showSectionGallery;
    this.showItemGallery = false;
  }

  gotoGallery() {
    this.router.navigate([`/inspection/review/gallery/`, this.inspection.id], { queryParamsHandling: 'merge' });
  }

  protected readonly length = length;
}
